import React from 'react';
import { Card, Table } from 'react-bootstrap';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  analyzeMvx,
  indexSegments,
  markerBar,
  MvxBarSegment,
  MvxZone,
  Specimen,
} from '../../models/MetabolicVulnerability';
import { MvxRangeChart, MvxChartBand } from '../charts/MvxRangeChart';
import { MvxSeverityBar } from '../charts/MvxSeverityBar';
import { MvxOptimalBar } from '../charts/MvxOptimalBar';
import { inputsFromBloodwork } from './metabolic-vulnerability-insights';

/**
 * MVX Plus — Results panel. Every marker in the section as a bar graph, in the
 * same `labsmartz-chart-table` layout the app uses for all other blood markers
 * (name + value on the left, a reference-range chart on the right), rendered via
 * `MvxRangeChart`. The 3 index scores (MVX/IVX/MMX) and the 6 factors all appear
 * as rows. Interpretation lives in the Insights panel — this is the numbers.
 */
interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
  specimen?: Specimen;
}

const FILL: Record<MvxZone, string> = { green: '#78F880', yellow: '#FEFA83', orange: '#FFD37E', red: '#FC6552' };

const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));

// Zone → the app's band-header label ("Alarm / < Lab / Lab / Optimal / …"),
// with orange split into "< Lab" (low side of optimal) vs "> Lab" (high side).
const bandLabel = (zone: MvxZone, isLowSide: boolean): string => {
  switch (zone) {
    case 'green': return 'Optimal';
    case 'yellow': return 'Lab';
    case 'orange': return isLowSide ? '< Lab' : '> Lab';
    case 'red': return 'Alarm';
    default: return '';
  }
};

// The four increasing-severity bars (MVX/IVX/MMX indices + GlycA) render RDW-style.
const SEVERITY_LABELS = ['Optimal', 'Lab', '> Lab', 'Alarm'];
// Small HDL-P is higher-better: the same bar mirrored, green Optimal on the right.
const SEVERITY_LABELS_REV = ['Alarm', '< Lab', 'Lab', 'Optimal'];
const severityBoundaries = (segs: MvxBarSegment[]): number[] => [
  segs[0].from,
  segs[0].to,
  segs[1].to,
  segs[2].to,
  segs[3].to,
];

const bandsFrom = (segments: MvxBarSegment[]): MvxChartBand[] => {
  const green = segments.find(s => s.zone === 'green');
  const greenFrom = green ? green.from : Infinity;
  return segments.map(s => ({
    color: FILL[s.zone],
    from: s.from,
    to: s.to,
    label: bandLabel(s.zone, s.to <= greenFrom + 1e-9),
  }));
};

// The band boundaries (excluding the outer padding edges) become the tick labels.
const ticksFrom = (segments: MvxBarSegment[], domLo: number, domHi: number): number[] => {
  const eps = (domHi - domLo) * 1e-6;
  const vals = new Set<number>();
  segments.forEach(s => {
    vals.add(s.from);
    vals.add(s.to);
  });
  return Array.from(vals)
    .filter(v => v > domLo + eps && v < domHi - eps)
    .sort((a, b) => a - b);
};

interface Row {
  id: string;
  name: string;
  valueLabel: string;
  domLo: number;
  domHi: number;
  bands: MvxChartBand[];
  ticks: number[];
  value: number;
  /** Present for the RDW-style severity bars (indices + GlycA, + mirrored sHDL-P). */
  severity?: { boundaries: number[]; labels: string[]; reversed?: boolean };
  /** Present for the symmetric optimal-in-the-middle factor bars (citrate, BCAAs). */
  optimal?: { optimal: [number, number]; reference: [number, number]; severe: [number, number] };
}

export class MvxResultsPanel extends React.Component<Props> {
  static shouldRender(ba: BloodworkAnalysis | undefined): boolean {
    return analyzeMvx(inputsFromBloodwork(ba)).anyEntered;
  }

  render() {
    const { bloodworkAnalysis, specimen } = this.props;
    const result = analyzeMvx(inputsFromBloodwork(bloodworkAnalysis), specimen);
    if (!result.anyEntered) return null;

    const enteredIndices = result.indices.filter(i => i.hasValue);
    // Sub-index scores are not capped at 100 (MMX seen at 110); share one scale.
    const subMax = Math.max(120, ...enteredIndices.map(i => Math.ceil(i.value * 1.05)));

    const rows: Row[] = [];
    enteredIndices.forEach(ix => {
      const segs = indexSegments(ix, specimen, subMax);
      rows.push({
        id: ix.id,
        name: ix.short,
        valueLabel: `${fmt(ix.value)} score`,
        domLo: 0,
        domHi: subMax,
        bands: bandsFrom(segs),
        ticks: ticksFrom(segs, 0, subMax),
        value: ix.value,
        severity: { boundaries: severityBoundaries(segs), labels: SEVERITY_LABELS },
      });
    });
    result.factors
      .filter(f => f.hasValue)
      .forEach(f => {
        const bar = markerBar(f, f.value);
        rows.push({
          id: f.id,
          name: f.short,
          valueLabel: `${fmt(f.value)} ${f.units}`,
          domLo: bar.domLo,
          domHi: bar.domHi,
          bands: bandsFrom(bar.segments),
          ticks: ticksFrom(bar.segments, bar.domLo, bar.domHi),
          value: f.value,
          // GlycA (lower-better) and small HDL-P (higher-better, mirrored) both
          // have the four-band shape — RDW-style, GlycA green-left, sHDL-P green-right.
          severity:
            f.direction === 'lowerBetter'
              ? { boundaries: severityBoundaries(bar.segments), labels: SEVERITY_LABELS }
              : f.direction === 'higherBetter'
              ? { boundaries: severityBoundaries(bar.segments), labels: SEVERITY_LABELS_REV, reversed: true }
              : undefined,
          // The optimal-in-the-middle factors (citrate, BCAAs) get the symmetric bar.
          optimal:
            f.direction === 'bandOptimal'
              ? { optimal: f.optimal, reference: f.reference, severe: f.severe }
              : undefined,
        });
      });

    const firstSeverity = rows.findIndex(r => r.severity);
    const firstOptimal = rows.findIndex(r => r.optimal);

    return (
      <Card className="evaluationPanel" id="mvx-results" style={{ width: 760, maxWidth: '100%', overflow: 'hidden' }}>
        <Card.Header className="evaluationPanelHeading">MVX Plus — Results</Card.Header>
        <Card.Body style={{ margin: 0, width: '100%' }}>
          <Table className="labsmartz-chart-table">
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id}>
                  <td className="text-nowrap ref-range-category">
                    {row.name}
                    <br />
                    <span className="ref-range-category-value">{row.valueLabel}</span>
                  </td>
                  <td>
                    {row.severity ? (
                      <MvxSeverityBar
                        boundaries={row.severity.boundaries}
                        labels={row.severity.labels}
                        reversed={row.severity.reversed}
                        value={row.value}
                        format={fmt}
                        showLabels={i === firstSeverity || !!row.severity.reversed}
                      />
                    ) : row.optimal ? (
                      <MvxOptimalBar
                        optimal={row.optimal.optimal}
                        reference={row.optimal.reference}
                        severe={row.optimal.severe}
                        value={row.value}
                        format={fmt}
                        showLabels={i === firstOptimal}
                      />
                    ) : (
                      <MvxRangeChart
                        domLo={row.domLo}
                        domHi={row.domHi}
                        bands={row.bands}
                        ticks={row.ticks}
                        value={row.value}
                        format={fmt}
                        showLabels={false}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div style={{ textAlign: 'center', fontSize: 10.5, color: '#999', marginTop: 6 }}>
            Optimal ranges are practitioner targets, tighter than the lab reference interval. Observational — not diagnostic.
          </div>
        </Card.Body>
      </Card>
    );
  }
}

export default MvxResultsPanel;
